import type { ReactNode } from 'react';
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@herit-fe/heritage-ui';
import { UI_TEXT } from '@/shared/languages';
import { DataTable } from './data-table';

export interface Column<T> {
  /** 데이터 객체의 필드 키. 셀/헤더 React key 로 사용. */
  key: string;
  /** 헤더 표시 — 텍스트 또는 노드(예: 전체선택 체크박스). */
  header: ReactNode;
  /** 컬럼/셀 Tailwind 클래스 (예: 'text-right', 'w-20'). */
  className?: string;
  /** 커스텀 셀 렌더러. 미지정 시 item[key] 를 그대로 출력. */
  render?: (item: T, index: number) => ReactNode;
}

export interface SimpleTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (item: T, index: number) => string | number;
  onRowClick?: (item: T) => void;
  /** 행 단위 className (데이터 의존). */
  rowClassName?: (item: T, index: number) => string | undefined;
  /**
   * 외곽 카드 chrome(`DataTable`) 래핑 여부(기본 true). 이미 카드(RegionCard/Card) 안에서 쓸 땐 false
   * 로 줘 카드 이중 테두리를 피한다(`DataListSection` 이 그렇게 전달).
   */
  shell?: boolean;
  /** 고정높이 세로 스크롤 + sticky thead/tfoot(`DataTable` scroll) — 고정높이 카드용. */
  scroll?: boolean;
  /** `scroll` 일 때 세로 고정높이 상한(px 숫자 또는 CSS 길이). 기본 20rem. */
  maxHeight?: number | string;
  /**
   * 합계(tfoot) 행 셀 — 컬럼과 1:1(길이=columns). 지정 시 표 하단에 총계행 렌더(예: '발전소 전체' + 합계값).
   * 각 셀은 해당 컬럼 className(정렬 등) 승계. `scroll` 과 함께면 하단 sticky.
   */
  footerCells?: ReactNode[];
}

/**
 * 컬럼 config 기반 단순 테이블 — 공용 `DataTable` 토대 위 합성. 컬럼 정의만 주면 되는 리스트 화면 SSOT.
 * 외곽 chrome·세로선·헤더배경·셀 패딩(행 높이)은 전부 `DataTable` 이 소유(`shell` 패스스루).
 * 페이지네이션·로딩·빈 상태는 호출처 책임(여기선 표만 그린다).
 */
export function SimpleTable<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  rowClassName,
  shell = true,
  scroll = false,
  maxHeight,
  footerCells,
}: SimpleTableProps<T>) {
  return (
    <DataTable shell={shell} scroll={scroll} maxHeight={maxHeight}>
      <TableHeader>
        <TableRow>
          {columns.map((col) => (
            <TableHead key={col.key} className={col.className}>
              {col.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((item, index) => (
          <TableRow
            key={rowKey(item, index)}
            className={[onRowClick ? 'cursor-pointer' : '', rowClassName?.(item, index) ?? '']
              .filter(Boolean)
              .join(' ')}
            onClick={onRowClick ? () => onRowClick(item) : undefined}
          >
            {columns.map((col) => (
              // col.className 이 정렬·폭 override. 셀 패딩(행 높이)은 DataTable 토대가 소유.
              <TableCell key={col.key} className={col.className}>
                {col.render
                  ? col.render(item, index)
                  : (((item as Record<string, unknown>)[col.key] as ReactNode) ??
                    UI_TEXT.COMMON_EMPTY_VALUE)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
      {footerCells ? (
        <TableFooter>
          <TableRow>
            {columns.map((col, colIndex) => (
              <TableCell key={col.key} className={col.className}>
                {footerCells[colIndex]}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      ) : null}
    </DataTable>
  );
}
